import { FunctionComponent, useEffect, useRef } from 'react';

import {
  betActions,
  useAudioTrigger,
  useAutoplaySlice,
  useBetSlice,
  useGameStateSlice,
  useMedia,
  useCoreSelector,
  chipActions,
} from '@pp/core';

import { Bets } from '../../../config/bets';
import { useGameDispatch } from '../../../store/GameStore';
import { useMWGameGridSlice } from '../../../store/slices/mwGameGrid/selectors';
import { BetSpotConnected } from '../betSpot/BetSpotConnected';
import { MWGameGridObserver } from '../MWGameGridObserver';
import { BetOnAllConnected } from '../betSpot/BetOnAllConnected';
import { BettingGrid, BettingGridProps } from './BettingGrid';

type ChipWeights = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
const getChipWeightFromIndex = (index: number): ChipWeights => (index + 1) as ChipWeights;

export interface BettingGridConnectedProps
  extends Omit<BettingGridProps, 'isCollapsed' | 'betLeft' | 'betRight' | 'gridRef' | 'targetScale' | 'targetShift'> {}

export const BettingGridConnected: FunctionComponent<BettingGridConnectedProps> = ({ ...rest }) => {
  const dispatch = useGameDispatch();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const isPortrait = useMedia('(orientation: portrait)');
  const rawTotalBet = useBetSlice.rawTotalBet();
  const targetScale = useMWGameGridSlice.getDimension('bettingGridScale');
  const targetShift = (useMWGameGridSlice.getDimension('wideChipStackHeight') || 0) / 2;
  const isAutoplay = useAutoplaySlice.isInProgress();
  const audioTrigger = useAudioTrigger();
  const balance = useGameStateSlice.getDeductibleBalance();
  const isLowBalance = useBetSlice.isLowBalance(balance);
  const bettingGridRef = useRef<HTMLDivElement>(null);
  const betSpotRef = useRef<HTMLDivElement>(null);
  const hasAutoplayJustStarted = useRef(true);
  const state = useCoreSelector((state) => state);

  let chipAmounts = state.config.tableConfig?.chipAmounts;
  useEffect(() => {
    let chipsLength = chipAmounts?.length || 0;
    let chipDiff = 8 - chipsLength;
    let arr: number[] = [];
    if (chipAmounts && chipDiff > 0) {
      for (let i = 0; i < chipDiff; i++) {
        arr.push((arr.length > 0 ? arr[arr.length - 1] : chipAmounts[chipAmounts.length - 1]) * 2);
      }
    }
    chipAmounts = chipAmounts?.concat(arr);
    if (chipAmounts) {
      dispatch(
        chipActions.setChips(
          chipAmounts.map((value, index) => ({
            value,
            weight: getChipWeightFromIndex(index),
            disabled: index + 1 > chipsLength,
            toolTipDisabled: index + 1 > chipsLength,
          }))
        )
      );
    }
  }, [chipAmounts]);

  useEffect(() => {
    dispatch(betActions.setOverridingTotalBet(rawTotalBet));
  }, [isBetsOpenStatus, rawTotalBet, dispatch]);

  // Play chip click sound only when the first autoplay bet is placed on bets open automatically
  useEffect(() => {
    let playChipSound = true;
    // Avoid playing chip sound when autoplay is started during bets open from the AP modal
    if (isAutoplay && hasAutoplayJustStarted.current) {
      hasAutoplayJustStarted.current = false;
      playChipSound = false;
    }

    if (isAutoplay && isBetsOpenStatus && playChipSound) {
      audioTrigger.play({ name: 'chipClick' });
    }
  }, [audioTrigger, isAutoplay, isBetsOpenStatus]);

  useEffect(() => {
    if (!isAutoplay) {
      hasAutoplayJustStarted.current = true;
    }
  }, [isBetsOpenStatus, isAutoplay]);

  useEffect(() => {
    MWGameGridObserver.addMeasurer('bettingGrid', () => {
      const rect = bettingGridRef.current?.getBoundingClientRect() || null;
      return {
        bettingGridWidth: bettingGridRef.current?.offsetWidth || null,
        bettingGridHeight: bettingGridRef.current?.offsetHeight || null,
        bettingGridLeft: rect?.left || null,
        bettingGridTop: rect?.top || null,
      };
    });
    MWGameGridObserver.addMeasurer('betSpot', () => ({
      betSpotSize: betSpotRef.current?.offsetHeight,
    }));
  }, []);

  return (
    <BettingGrid
      isCollapsed={!isBetsOpenStatus || isLowBalance}
      gridRef={bettingGridRef}
      targetScale={targetScale}
      targetShift={targetShift + 20}
      {...rest}
    >
      <BetSpotConnected betSpot={Bets.One} betSpotRef={betSpotRef} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Two} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Five} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Eight} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Ten} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Fifteen} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Twenty} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Thirty} clickSound="betSpotNumber" />
      <BetSpotConnected betSpot={Bets.Forty} clickSound="betSpotNumber" />
      {isPortrait && <BetOnAllConnected clickSound="betSpotNumber" />}
    </BettingGrid>
  );
};
